import {
  ActionFunctionArgs,
  LoaderFunction,
  LoaderFunctionArgs,
  Params,
  redirect,
} from 'react-router-dom';
import { FirestoreError } from 'firebase/firestore';
import { FunctionsError } from 'firebase/functions';
import { isInvitation } from '@utils/typeGuards';
import { InvitationFromStore, PermissionFromStore, UserRole } from '@/types/types';
import { useOrganizationStore } from '@/stores/organizationStore';
import { assert } from '@/utils/asserts';
import { WorkspacesService } from '@/services/workspacesService';
import { WorkspaceSettingsView } from './WorkspaceSettingsView';
import { useWorkspaceIdStore } from '@/stores/workspaceIdStore';
import { showToast } from '@/utils/toasts';
import { formDataToObject, mapErrorMessage } from '@/utils/helpers';
import { sanitizeCallableErrorMessage } from '@/utils/sanitizeCallableErrorMessage';
import { InvitationsSchema } from '@/components/layouts/Modals/InviteUsersModal/InviteUsersModal';

export function getTableUsers(
  permissions: PermissionFromStore[],
  invitations: InvitationFromStore<'workspace' | 'organization'>[],
) {
  return [...permissions, ...invitations].map((per) => {
    if (isInvitation(per)) {
      return {
        email: per.invitedUserEmail,
        id: per.id,
        hash: per.hash,
        name: per.name,
        isInvitation: true,
        role: per.role,
      };
    } else {
      return {
        id: per.id,
        email: per.email,
        role: per.role,
        name: per.name,
        isInvitation: false,
      };
    }
  });
}

export const WorkspaceSettings = () => {
  return <WorkspaceSettingsView />;
};

const permissionsLoader: LoaderFunction = async ({ request }) => {
  const workspaceId = new URL(request.url).searchParams.get('workspaceId');
  if (!workspaceId)
    throw { code: 'no-workspace-id', message: 'No workspace id provided' };
  return WorkspacesService.getPermissions(workspaceId);
};

const invitationsLoader: LoaderFunction = async ({ request }) => {
  const workspaceId = new URL(request.url).searchParams.get('workspaceId');
  if (!workspaceId)
    throw { code: 'no-workspace-id', message: 'No workspace id provided' };
  return await WorkspacesService.getInvitations(workspaceId);
};

export const workspaceSettingsLoader = async (args: LoaderFunctionArgs) => {
  const [permissions, invitations] = await Promise.all([
    permissionsLoader(args),
    invitationsLoader(args),
  ]);
  return { permissions, invitations };
};

type PUTRequestType = 'updateEntityName' | 'changeUserRole';
type DELETERequestType = 'invitation' | 'permission' | 'workspace' | 'selectedUsers';

export async function workspaceSettingsActions({ params, request }: ActionFunctionArgs) {
  const data = await request.formData();
  if (request.method === 'DELETE') {
    const reqType = data.get('type') as DELETERequestType;
    return await handleDeletion(reqType, request, data);
  } else if (request.method === 'POST') {
    return await createWorkspaceInvitations(request, data);
  } else if (request.method === 'PUT') {
    const reqType = data.get('type') as PUTRequestType;
    return await handleUpdating(params, request, data, reqType);
  }

  return null;
}

async function handleUpdating(
  params: Params,
  request: Request,
  data: FormData,
  reqType: PUTRequestType,
) {
  const workspaceId = new URL(request.url).searchParams.get('workspaceId') as string;
  if (reqType === 'updateEntityName') {
    return await updateWorkspaceName(data, workspaceId);
  } else {
    return await changeUserRole(data, workspaceId);
  }
}

async function updateWorkspaceName(data: FormData, workspaceId: string) {
  try {
    const name = data.get('name') as string;
    return await WorkspacesService.updateWorkspaceName(workspaceId, name);
  } catch (e) {
    const error = e as FirestoreError;
    showToast('Error!', 'Failed to update workspace name. Try again later!', 'error');
    console.error(error);
    return { message: sanitizeCallableErrorMessage(e) };
  }
}

async function changeUserRole(data: FormData, workspaceId: string) {
  try {
    const permissionId = data.get('permissionId') as string;
    const role = data.get('role') as string;
    await WorkspacesService.changeRole(workspaceId, permissionId, role);
    return null;
  } catch (e) {
    console.error(e);
    showToast('Error!', 'Failed to update user role. Try again later!', 'error');
    return { changeRoleError: { message: sanitizeCallableErrorMessage(e) } };
  }
}

async function createWorkspaceInvitations(request: Request, formData: FormData) {
  try {
    const workspaceId = new URL(request.url).searchParams.get('workspaceId') as string;
    const organization = useOrganizationStore.getState().selectedOrganization;
    const organizationId = organization?.id;
    assert(
      typeof organizationId !== 'undefined' && organizationId !== null,
      'organizationId is undefined',
    );
    const data = formDataToObject<
      Omit<InvitationsSchema, 'emails'> & { emails: string[] }
    >(formData);
    const emails = data.emails;
    const role = data.role;
    return await WorkspacesService.createInvitations(emails, workspaceId, role);
  } catch (e) {
    const error = e as FunctionsError;
    const message = mapErrorMessage(e);
    showToast('Error!', message, 'error');
    console.error(error);
    return { createInvitationError: { message } };
  }
}

async function handleDeletion(
  reqType: DELETERequestType,
  request: Request,
  data: FormData,
) {
  if (reqType === 'invitation') {
    return await deleteInvitation(reqType, request, data);
  } else if (reqType === 'permission') {
    return await deletePermission(request, data);
  } else if (reqType === 'selectedUsers') {
    return await deleteSelected(request, data);
  } else if (reqType === 'workspace') {
    return await deleteWorkspace(request);
  }
}

async function deleteSelected(request: Request, data: FormData) {
  const workspaceId = new URL(request.url).searchParams.get('workspaceId') as string;
  const selected = JSON.parse(data.get('selected') as string) as {
    id: string;
    role: UserRole | string;
  }[];
  try {
    return await WorkspacesService.deleteSelectedUsers(workspaceId, selected);
  } catch (e) {
    const error = e as FirestoreError;
    showToast('Error!', 'Failed to delete selected users. Try again later!', 'error');
    console.error(error);
    return { deleteSelectedError: { message: sanitizeCallableErrorMessage(e) } };
  }
}

async function deleteInvitation(reqType: string, request: Request, data: FormData) {
  const invitationId = data.get('invitationId') as string;
  const workspaceId = new URL(request.url).searchParams.get('workspaceId') as string;
  try {
    return await WorkspacesService.deleteInvitation(invitationId, workspaceId);
  } catch (e) {
    const error = e as FirestoreError;
    showToast('Error!', 'Failed to delete invitation. Try again later!', 'error');
    console.error(error);
    return { deleteInvitationError: { message: sanitizeCallableErrorMessage(e) } };
  }
}

async function deletePermission(request: Request, data: FormData) {
  const workspaceId = new URL(request.url).searchParams.get('workspaceId') as string;
  const permissionId = data.get('permissionId') as string;
  try {
    await WorkspacesService.deletePermission(permissionId, workspaceId);
    return redirect('/');
  } catch (e) {
    console.error(e);
    showToast('Error!', 'Failed to delete selected user. Try again later!', 'error');
    return { deletePermissionError: { message: sanitizeCallableErrorMessage(e) } };
  }
}

async function deleteWorkspace(request: Request) {
  const workspaceId = new URL(request.url).searchParams.get('workspaceId') as string;
  const organization = useOrganizationStore.getState().selectedOrganization;
  assert(organization, 'organization is undefined');
  try {
    await WorkspacesService.deleteWorkspace(
      workspaceId,
      organization?.id,
      organization?.region,
    );
    useWorkspaceIdStore.getState().resetWorkspaceId();
    return redirect('/');
  } catch (e) {
    const error = e as FirestoreError;
    showToast('Error!', 'Failed to delete workspace. Try again later!', 'error');
    console.error(error);
    return { deleteWorkspaceError: { message: sanitizeCallableErrorMessage(e) } };
  }
}
